import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import axiosInstance from "../../../httpInterceptor";

interface FAQ {
  serial_no: number;
  Question: string;
  Answer: string;
}

const FAQ: React.FC = () => {
  const [faqData, setFaqData] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosInstance.get(
        `${process.env.REACT_APP_FAQ_SERVICE_BASE_URL}/faqs`
      );

      if (response.data.success) {
        setFaqData(response.data.data);
      } else {
        setError("Failed to load FAQs");
      }
    } catch (err) {
      console.error("Error fetching FAQs:", err);
      setError("Failed to load FAQs. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            backgroundColor: "#ffffff",
            boxShadow: "0 2px 8px rgba(66, 24, 24, 0.08)",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <HelpOutlineRoundedIcon
              sx={{
                fontSize: 40,
                color: "#FF5E4F",
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#0F172A",
              }}
            >
              Frequently Asked Questions
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: "#64748B",
              fontSize: "1.05rem",
            }}
          >
            Find answers to common questions about using the PBT Assessment
            platform. If you can't find what you're looking for, please contact
            your administrator.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress style={{color:"#FF5E4F"}} />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* FAQ Accordions */}
          {!loading && !error && faqData.length === 0 && (
            <Alert severity="info">No FAQs available at the moment.</Alert>
          )}

          {!loading && !error && faqData.length > 0 && (
            <Box sx={{ mt: 3 }}>
              {faqData.map((faq) => (
                <Accordion
                  key={faq.serial_no}
                  disableGutters
                  sx={{
                    mb: 2,
                    borderRadius: "8px !important",
                    border: "1px solid #E2E8F0",
                    boxShadow: "none",
                    "&:before": {
                      display: "none",
                    },
                    "&.Mui-expanded": {
                      margin: "0 0 16px 0",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: "#000000" }} />
                    }
                    sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "#ffdad6ff",
                      },
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "1.05rem",
                        color: "#0F172A",
                      }}
                    >
                      {faq.Question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      pt: 2,
                      pb: 3,
                      px: 3,
                      backgroundColor: "#FAFBFC",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#475569",
                        lineHeight: 1.7,
                        fontSize: "0.95rem",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {faq.Answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {/* Contact Section */}
          <Box
            sx={{
              mt: 5,
              p: 3,
              backgroundColor: "#F1F5F9",
              borderRadius: 2,
              borderLeft: "4px solid #fd4b3aff",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#0F172A",
                mb: 1,
              }}
            >
              Still have questions?
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748B",
              }}
            >
              If you can't find what you're looking for, please contact the Delivery Excellence team at{" "}
              <a href="mailto:DL.Delex@ltimindtree.com">DL.Delex@ltimindtree.com</a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default FAQ;
